import React, { useRef, useState } from "react";
import { Formik, Field, Form, ErrorMessage, FormikHelpers } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Row, Col, Button } from "react-bootstrap";
import CommentField from "./inputs/CommentField ";
import { clientFormValidationSchema, clientFormValidationSchemaWithoutContacts } from "./constants";
import DatePicker from "./inputs/DatePicker";
import Spinner from "@services/ui-components/src/Spinner/Spinner";
import { PhonePicker, ReactSelect, SpinnerLoader, TextInput } from "@services/ui-components";
import { ApolloError, useReactiveVar } from "@apollo/client";
import ErrorService from "../../services/ErrorService";
import { ErrorModalComponent } from "@me-pos/error-modal";
import i18next from "i18next";
import ImageCrop from "@services/ui-components/src/ImageCrop/ImageCrop";
import { PhoneInputRefType } from "react-international-phone";
import {
    useCreateClientMutation,
    useGetGenderQuery,
    useGetIsoCodeForClientQuery,
    useGetOneClientQuery,
    useUpdateClientMutation,
} from "@me-team/host/main/clients/graphql/clients.hooks";
import { ClientInput } from "@me-team/host/main/graphql/types";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import ContactsFallback from "../Clients/ContactsFallback";

interface ValidationErrorExtensions {
    validation: {
        phone?: { message: string }[];
        email?: { message: string }[];
        image?: { message: string }[];
    };
}

interface Option {
    value: number;
    label: string;
}

interface FormValues {
    comment?: string;
    dateOfBirth?: string;
    email: string;
    gender: number | null;
    image?: File | null;
    isUpdateImage?: boolean;
    name: string;
    patronymic: string;
    phone: string;
    surname: string;
}

interface CreateUpdateClientFormProps {
    isFormReadOnly?: boolean;
    shouldShowClientContacts?: boolean;
}

type SetFieldValueType = (field: string, value: string | boolean | string[] | File | (string | File)[]) => void;

const CreateUpdateClientForm: React.FC<CreateUpdateClientFormProps> = ({
    isFormReadOnly = false,
    shouldShowClientContacts = false,
}) => {
    const { t } = useTranslation();
    const { branchId, id } = useParams();
    const isUpdateClientForm: boolean = !!id;
    const navigate = useNavigate();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const [isErrorModalOpen, setIsErrorModalOpen] = useState<number>(null);
    const phonePickerRef = useRef<PhoneInputRefType>(null);

    const { data: oneClientData, loading } = useGetOneClientQuery({
        variables: {
            id: +id,
            companyId: +currentCompanyId,
            branchId: Number(branchId),
        },
        skip: !currentCompanyId || !id,
        context: {
            errorType: "local",
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });

    const { data: genderData } = useGetGenderQuery({
        context: {
            errorType: "local",
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });

    const { data: isoCodeData, loading: isoCodeLoading } = useGetIsoCodeForClientQuery({
        variables: {
            companyId: Number(currentCompanyId),
        },
        skip: !currentCompanyId,
        context: {
            errorType: "local",
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });
    const phoneCode = isoCodeData?.user?.company?.country?.isoCode.toLowerCase();

    const [createClient] = useCreateClientMutation();
    const [updateClient] = useUpdateClientMutation();

    const client = oneClientData?.user?.company?.clients?.clients[0];

    const [setSelectedOption] = useState<number>(id ? client?.gender?.id : null);

    const options: Option[] = genderData?.gender.map((item) => ({
        value: item.id,
        label: t(item.name),
    }));

    const handlePhoto = (setFieldValue: SetFieldValueType) => {
        setFieldValue("isUpdateImage", true);
    };

    const initialValues = {
        name: id && client?.name ? client?.name : "",
        surname: id && client?.surname ? client?.surname : "",
        patronymic: id && client?.patronymic ? client?.patronymic : "",
        dateOfBirth: id && client?.dateOfBirth ? client?.dateOfBirth : "",
        email: id && client?.email ? client?.email : "",
        phone: id && client?.phone ? client?.phone : null,
        comment: id && client?.comment ? client?.comment : "",
        gender: id && client?.gender?.id ? client?.gender?.id : null,
        isUpdateImage: false,
    };
    const shouldUseContactFallbackValues = isUpdateClientForm && !shouldShowClientContacts;
    const validationSchema = shouldUseContactFallbackValues
        ? clientFormValidationSchemaWithoutContacts(t)
        : clientFormValidationSchema(t);

    const handleUpdateSubmit = async (values: FormValues, { setSubmitting, setErrors }: FormikHelpers<FormValues>) => {
        if (isFormReadOnly) {
            setSubmitting(false);
            return;
        }

        const emailValue = shouldUseContactFallbackValues ? (client?.email ?? values?.email) : values?.email;
        const phoneValue = shouldUseContactFallbackValues ? (client?.phone ?? values?.phone) : values?.phone;

        const input: ClientInput = {
            id: +id,
            company: +currentCompanyId,
            name: values?.name,
            surname: values?.surname,
            patronymic: values?.patronymic,
            email: emailValue,
            phone: phoneValue,
            comment: values?.comment,
            dateOfBirth: values?.dateOfBirth,
            gender: values?.gender ? values?.gender : null,
            isUpdateImage: values?.isUpdateImage,

            ...(values?.image && { image: values?.image }),
        };

        await updateClient({
            variables: { id: +id, input, companyId: +currentCompanyId, branchId: +branchId },
            context: {
                errorType: "local",
            },
            onCompleted: (data) => {
                data &&
                    navigate(`/clients/${currentCompanyId}/list/${branchId}`, {
                        state: { text: t("Saved"), toast: true },
                    });
                data && setSubmitting(false);
            },
            onError: (error) => {
                console.error("error", error.graphQLErrors[0]);
                const validationErrors = (error.graphQLErrors[0].extensions as unknown as ValidationErrorExtensions)
                    .validation;
                validationErrors?.phone &&
                    validationErrors?.phone[0]?.message === "This value is not a" + " valid phone number." &&
                    setErrors({ phone: t("validation.this value is not a valid phone number") });
                validationErrors?.email &&
                    validationErrors?.email[0].message === "This value is not a" + " valid email address." &&
                    setErrors({ email: t("validation.This value is not a valid email address.") });

                validationErrors?.phone &&
                    validationErrors?.phone[0]?.message === "Client already exists by provided phone." &&
                    setErrors({ phone: t("validation.there is already a customer with this phone number") });
                validationErrors?.email &&
                    validationErrors?.email[0].message === "Client already exists by provided email." &&
                    setErrors({ email: t("validation.there is already a client with this email") });

                validationErrors?.image &&
                    validationErrors?.image[0].message.replace(/\(\d+px\)/, "(width)") ===
                        "The image width is too small (width). Minimum width" + " expected is 500px." &&
                    setErrors({ image: t("The image width is too small. Minimum width expected is 500px.") });

                setIsErrorModalOpen(ErrorService.errorHandling(error));
                setSubmitting(false);
            },
        });
    };

    const handleCreateSubmit = async (values: FormValues, { setSubmitting, setErrors }: FormikHelpers<FormValues>) => {
        const input: ClientInput = {
            company: +currentCompanyId,
            name: values?.name,
            surname: values?.surname ? values?.surname : null,
            patronymic: values?.patronymic ? values?.patronymic : null,
            email: values?.email ? values?.email : null,
            phone: values?.phone ? values?.phone : null,
            comment: values?.comment ? values?.comment : null,
            image: values?.image ? values?.image : null,
            dateOfBirth: values?.dateOfBirth ? values?.dateOfBirth : null,
            gender: values?.gender ? values?.gender : null,
        };

        const errorMappings: Record<string, { field: keyof FormValues; message: string }> = {
            "This value is not a valid phone number.": {
                field: "phone",
                message: t("validation.this value is not a valid phone number"),
            },
            "This value is not a valid email address.": {
                field: "email",
                message: t("validation.This value is not a valid email address."),
            },
            "Client already exists by provided phone.": {
                field: "phone",
                message: t("validation.there is already a customer with this phone number"),
            },
            "Client already exists by provided email.": {
                field: "email",
                message: t("validation.there is already a client with this email"),
            },
            "The image width is too small (width). Minimum width expected is 500px.": {
                field: "image",
                message: t("The image width is too small. Minimum width expected is 500px."),
            },
        };
        await createClient({
            variables: { companyId: +currentCompanyId, input, branchId: +branchId },
            context: {
                errorType: "local",
            },
            onCompleted: (data) => {
                data &&
                    navigate(`/clients/${currentCompanyId}/list/${branchId}`, {
                        state: { text: t("Created"), toast: true },
                    });
                setSubmitting(false);
            },
            onError: (error: ApolloError) => {
                const validationErrors = (error.graphQLErrors[0].extensions as unknown as ValidationErrorExtensions)
                    .validation;

                Object.entries(validationErrors || {}).forEach(([field, messages]) => {
                    const message = messages[0]?.message;
                    const mappedError = errorMappings[message];

                    if (mappedError && mappedError.field === field) {
                        setErrors({ [field]: mappedError.message });
                    }
                });
                setIsErrorModalOpen(ErrorService.errorHandling(error));
                setSubmitting(false);
            },
        });
    };

    if (loading || !currentCompanyId || isoCodeLoading) return <Spinner />;

    return (
        <>
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={id ? handleUpdateSubmit : handleCreateSubmit}
            >
                {({ setFieldValue, errors, values, handleBlur, touched, isSubmitting }) => {
                    const handleSelectChange = (selectedOption: Option | null) => {
                        setFieldValue("gender", selectedOption ? selectedOption?.value : "");
                        setSelectedOption(selectedOption ? selectedOption?.value : null);
                    };

                    const handleCancel = (e: React.MouseEvent<HTMLButtonElement>) => {
                        e.preventDefault();
                        navigate(-1);
                    };

                    return (
                        <Form>
                            <Row className="flex-column flex-lg-row mb-3 mb-lg-5">
                                <Col lg={9} className="pe-lg-5">
                                    <h4 className="mb-3">{t("Personal data")}</h4>
                                    <Row className="mb-3 mb-sm-4 fs-7">
                                        <TextInput
                                            id="client-name-field"
                                            name="name"
                                            placeholder="Add Name"
                                            label={<span>{t("Name")}</span>}
                                            maxLength={50}
                                            disabled={isFormReadOnly}
                                            required
                                        />
                                    </Row>
                                    <Row className="mb-3 mb-sm-4 fs-7">
                                        <TextInput
                                            id="client-surname-field"
                                            name="surname"
                                            placeholder="Add Last name"
                                            label={t("Last name")}
                                            maxLength={50}
                                            disabled={isFormReadOnly}
                                        />
                                    </Row>
                                    <Row className="mb-3 mb-sm-4 fs-7">
                                        <TextInput
                                            id="client-patronymic-field"
                                            name="patronymic"
                                            placeholder="Add Middle name"
                                            label={t("Middle name")}
                                            maxLength={50}
                                            disabled={isFormReadOnly}
                                        />
                                    </Row>
                                    <Row className="mb-3 mb-sm-4">
                                        <DatePicker
                                            id="client-dateOfBirth-field"
                                            classes="datepicker-with-icon"
                                            name="dateOfBirth"
                                            label={t("Date of birth")}
                                            placeholder={t("dd.mm.yyyy")}
                                            value={id && client?.dateOfBirth}
                                            disabled={isFormReadOnly}
                                            todayButton={t("Today")}
                                            isClearable
                                        />
                                    </Row>
                                    <Row className="mb-2">
                                        <Col sm={3} md={2} className="mb-2 mb-sm-0 d-flex align-items-center">
                                            <label
                                                htmlFor="client-email-field"
                                                className="text-dark fs-7 fw-bold required-label"
                                            >
                                                {t("E-mail")}
                                            </label>
                                        </Col>
                                        <Col sm={9} md={10}>
                                            {isUpdateClientForm && !shouldShowClientContacts ? (
                                                <div className="form-control fs-7 d-flex align-items-center">
                                                    <ContactsFallback />
                                                </div>
                                            ) : (
                                                <>
                                                    <Field
                                                        id="client-email-field"
                                                        name="email"
                                                        placeholder={t("Add E-mail")}
                                                        className={`form-control fs-7  ${
                                                            errors?.email && touched?.email ? "is-invalid" : ""
                                                        }`}
                                                        onBlur={handleBlur}
                                                        disabled={isFormReadOnly}
                                                    />
                                                    <ErrorMessage name="email">
                                                        {(msg) => <div className="invalid-feedback">{msg}</div>}
                                                    </ErrorMessage>
                                                </>
                                            )}
                                        </Col>
                                    </Row>
                                    <Col className="text-center text-grey mb-sm-2">
                                        <span>{t("or")}</span>
                                    </Col>
                                    <Row className="mb-3 mb-sm-4">
                                        <Col sm={3} md={2} className="mb-2 mb-sm-0 d-flex align-items-center">
                                            <label
                                                htmlFor="client-phone-field"
                                                className="text-dark fs-7 fw-bold required-label"
                                                onClick={() => phonePickerRef.current?.focus()}
                                            >
                                                {t("phone")}
                                            </label>
                                        </Col>
                                        <Col sm={9} md={10}>
                                            {isUpdateClientForm && !shouldShowClientContacts ? (
                                                <div className="form-control fs-7 d-flex align-items-center">
                                                    <ContactsFallback />
                                                </div>
                                            ) : (
                                                <>
                                                    <PhonePicker
                                                        phoneRef={phonePickerRef}
                                                        name="phone"
                                                        value={id ? client?.phone : ""}
                                                        disabled={isFormReadOnly}
                                                        id="client-phone-field"
                                                        defaultCountry={phoneCode}
                                                    />
                                                    <ErrorMessage name="phone">
                                                        {(msg) => <div className="fs-7 m-0 p-1 text-danger">{msg}</div>}
                                                    </ErrorMessage>
                                                </>
                                            )}
                                        </Col>
                                    </Row>
                                    <Row className="mb-sm-4">
                                        <Col sm={3} md={2} className="mb-2 mb-sm-0 d-flex align-items-center">
                                            <label htmlFor="gender" className="text-dark fs-7 fw-bold">
                                                {t("Gender")}
                                            </label>
                                        </Col>
                                        <Col sm={9} md={10}>
                                            <ReactSelect
                                                id="client-gender-field"
                                                name="gender"
                                                placeholder={t("Select gender")}
                                                options={options}
                                                value={options?.find((option: any) => option.value === values.gender)}
                                                onChange={handleSelectChange}
                                                isSearchable={false}
                                                disabled={isFormReadOnly}
                                            />
                                            <ErrorMessage name="gender">
                                                {(msg) => <div className="invalid-feedback">{msg}</div>}
                                            </ErrorMessage>
                                        </Col>
                                    </Row>
                                    <Row className="mb-3 mb-sm-4">
                                        <CommentField
                                            id="client-comment-field"
                                            label={t("Comment")}
                                            name="comment"
                                            placeholder={t("Add a comment")}
                                            disabled={isFormReadOnly}
                                        />
                                    </Row>
                                </Col>
                                <Col lg={4} xxl={3}>
                                    <div className="d-flex mb-3 w-100 flex-column  justify-content-lg-end">
                                        <h4 className="mb-3 w-100">{t("Clients photo")}</h4>
                                        <ImageCrop
                                            previewPhoto={id && client?.image}
                                            setFieldValue={setFieldValue}
                                            fieldSetter={() => handlePhoto(setFieldValue)}
                                            fieldName="image"
                                            dismissImage={() => setFieldValue("isUpdateImage", true)}
                                            modalTitle={t("Clients photo")}
                                            notBGinfo={true}
                                            disabled={isFormReadOnly}
                                        />
                                    </div>
                                </Col>
                            </Row>

                            <div className="d-sm-flex my-4 mt-lg-0">
                                <Button
                                    variant="outline-primary"
                                    className="custom-btn mb-3 mb-sm-0 me-3"
                                    type="reset"
                                    onClick={handleCancel}
                                >
                                    {t("cancel")}
                                </Button>

                                {id ? (
                                    <Button
                                        variant="primary"
                                        type="submit"
                                        className="custom-btn"
                                        disabled={isFormReadOnly || isSubmitting}
                                    >
                                        {isSubmitting ? (
                                            <SpinnerLoader variant="light" loadingText={t("Saving...")} />
                                        ) : (
                                            t("save")
                                        )}
                                    </Button>
                                ) : (
                                    <Button
                                        variant="primary"
                                        type="submit"
                                        className="custom-btn"
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? (
                                            <SpinnerLoader variant="light" loadingText={t("Creating...")} />
                                        ) : (
                                            t("Create")
                                        )}
                                    </Button>
                                )}
                            </div>
                        </Form>
                    );
                }}
            </Formik>
            {isErrorModalOpen ? (
                <ErrorModalComponent
                    i18n={i18next}
                    onClose={() => {
                        setIsErrorModalOpen(null);
                    }}
                    isOpen={!!isErrorModalOpen}
                    currentError={isErrorModalOpen}
                />
            ) : null}
        </>
    );
};
export default CreateUpdateClientForm;
